<template>
  <div
    class="base-video-player"
    :class="{ fullscreen: isFullscreen }"
    ref="playerContainer"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
  >
    <div class="video-wrapper">
      <video
        ref="videoRef"
        class="video-element"
        :src="src"
        :poster="poster"
        preload="metadata"
        @timeupdate="handleTimeUpdate"
        @loadedmetadata="handleLoadedMetadata"
        @ended="handleEnded"
        @pause="handlePause"
        @play="handlePlay"
        @waiting="handleWaiting"
        @canplay="handleCanPlay"
        @canplaythrough="handleCanPlayThrough"
        @loadstart="handleLoadStart"
        @progress="handleProgress"
        @error="handleError"
        :controls="false"
        :autoplay="false"
      ></video>

      <div class="video-overlay" v-if="!src || loading">
        <span v-if="loading">视频加载中...</span>
        <span v-else>暂无可播放视频</span>
      </div>

      <div class="video-loading" v-if="videoLoading && src && !showCenterPlayButton">
        <div class="loading-spinner">
          <div class="spinner-circle"></div>
          <div class="spinner-circle"></div>
          <div class="spinner-circle"></div>
        </div>
        <span class="loading-text">{{ loadingText }}</span>
      </div>

      <div class="center-play-button" v-if="showCenterPlayButton" @click.stop="togglePlay">
        <img :src="playIcon" alt="play" class="play-icon" />
      </div>
      <div class="resume-tip-wrapper">
        <resume-playback-tip
          v-if="resumeTipVisible && resumeTime > 0"
          :visible="resumeTipVisible"
          :resume-time="resumeTime"
          @resume="handleResume"
        />
      </div>

      <div class="control-layer" :class="{ visible: controlVisible }">
        <video-control-bar
          :visible="controlVisible"
          :title="title"
          :current-time="currentTime"
          :duration="duration"
          :is-playing="isPlaying"
          :show-prev="normalizedFeatures.prev"
          :show-next="normalizedFeatures.next"
          :show-fullscreen="normalizedFeatures.fullscreen"
          :disable-prev="disablePrev"
          :disable-next="disableNext"
          :disable-fullscreen="!normalizedFeatures.fullscreen"
          :disabled-play="!canPlay"
          :loading="loading"
          :is-fullscreen="isFullscreen"
          :chapters="chapters"
          :current-index="currentIndex"
          @prev="handlePrev"
          @next="handleNext"
          @togglePlay="togglePlay"
          @seek="handleSeek"
          @fullscreen="toggleFullscreen"
          @selectChapter="handleSelectChapter"
          @chapterScroll="handleMouseMove"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VideoControlBar from './VideoControlBar.vue';
import ResumePlaybackTip from './ResumePlaybackTip.vue';
import playIcon from './images/ic_play.svg';

const defaultFeatures = {
  prev: true,
  next: true,
  fullscreen: true,
  keyboard: true,
  autoHide: true,
};

export default {
  name: 'BaseVideoPlayer',
  components: { VideoControlBar, ResumePlaybackTip },
  props: {
    chapters: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    src: {
      type: String,
      default: '',
    },
    poster: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disablePrev: {
      type: Boolean,
      default: false,
    },
    disableNext: {
      type: Boolean,
      default: false,
    },
    features: {
      type: Object,
      default: () => ({}),
    },
    resumeTime: {
      type: Number,
      default: 0,
    },
    showResumeTip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      showControls: true,
      controlsTimer: null,
      isFullscreen: false,
      playIcon,
      resumeTipVisible: false,
      initialTimeSet: false,
      videoLoading: false,
      loadingText: '加载中...',
      loadingTimer: null,
      seekTipText: '',
      seekTipIcon: '',
      cursorTimer: null,
      originalCursor: null,
    };
  },
  computed: {
    normalizedFeatures() {
      return {
        ...defaultFeatures,
        ...(this.features || {}),
      };
    },
    canPlay() {
      return Boolean(this.src) && !this.loading;
    },
    showCenterPlayButton() {
      return this.canPlay && !this.isPlaying && !this.loading;
    },
    controlVisible() {
      return this.showControls || !this.isPlaying;
    }
  },
  watch: {
    src(val) {
      this.resetVideoState();
      this.resumeTipVisible = false;
      this.videoLoading = false;
      clearTimeout(this.loadingTimer);
    },
    features: {
      handler() {
        this.applyAutoHideSetting();
      },
      deep: true,
    },
    showResumeTip(newVal) {
      if (newVal && this.resumeTime > 0 && this.duration > 0) {
        const video = this.$refs.videoRef;
        if (video && video.readyState >= 1) {
          video.currentTime = this.resumeTime;
          this.currentTime = this.resumeTime;
          this.resumeTipVisible = true;
        }
      }
    },
  },
  methods: {
    resetVideoState() {
      const video = this.$refs.videoRef;
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.load();
      }
      this.currentTime = 0;
      this.duration = 0;
      this.isPlaying = false;
    },
    handleTimeUpdate(event) {
      this.currentTime = event.target.currentTime;
      this.$emit('timeupdate', this.currentTime);
    },
    handleLoadedMetadata(event) {
      this.duration = event.target.duration || 0;
      this.$emit('loadedmetadata', this.duration);
      // 如果有恢复播放时间且未设置过，设置初始播放时间并显示提示
      if (this.resumeTime > 0 && this.showResumeTip) {
        const video = this.$refs.videoRef;
        if (video) {
          video.currentTime = this.resumeTime;
          this.currentTime = this.resumeTime;
          this.resumeTipVisible = true;
        }
      }
    },
    handleLoadStart() {
      // 开始加载时显示加载状态（仅在视频未准备好时）
      const video = this.$refs.videoRef;
      if (video && video.readyState < 2) {
        this.videoLoading = true;
        this.loadingText = '加载中...';
      }
    },
    handleWaiting() {
      // 视频缓冲时显示加载状态
      if (this.isPlaying) {
        this.videoLoading = true;
        this.loadingText = '缓冲中...';
      }
    },
    handleCanPlay() {
      // 视频可以播放时，延迟隐藏加载状态，确保流畅过渡
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => {
        // 只有在视频确实可以播放时才隐藏
        const video = this.$refs.videoRef;
        if (video && video.readyState >= 2) {
          this.videoLoading = false;
        }
      }, 300);
      // this.$refs.videoRef?.play();
    },
    handleCanPlayThrough() {
      // 视频可以流畅播放时，立即隐藏加载状态
      clearTimeout(this.loadingTimer);
      this.videoLoading = false;
    },
    handleProgress() {
      // 加载进度更新时，如果缓冲足够，可以隐藏加载状态
      const video = this.$refs.videoRef;
      if (video && video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const currentTime = video.currentTime;
        const duration = video.duration;
        // 如果已缓冲的内容足够播放一段时间，隐藏加载状态
        if (bufferedEnd - currentTime > 3 || bufferedEnd >= duration * 0.9) {
          clearTimeout(this.loadingTimer);
          this.loadingTimer = setTimeout(() => {
            if (!video.readyState || video.readyState >= 3) {
              this.videoLoading = false;
            }
          }, 200);
        }
      }
    },
    handleError(event) {
      // 视频加载错误时隐藏加载状态
      this.videoLoading = false;
      this.$emit('error', event);
    },
    handleEnded() {
      this.isPlaying = false;
      this.$emit('ended');
    },
    handlePause() {
      this.isPlaying = false;
      this.$emit('pause');
    },
    handlePlay() {
      this.resumeTipVisible = false;
      this.isPlaying = true;
      // 如果视频可以播放，隐藏加载状态
      const video = this.$refs.videoRef;
      if (video && video.readyState >= 3) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = setTimeout(() => {
          this.videoLoading = false;
        }, 200);
      }
      this.$emit('play');
    },
    togglePlay() {
      if (!this.canPlay) return;
      const video = this.$refs.videoRef;
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    handleSeek(percent) {
      if (!this.duration || !this.canPlay) return;
      const video = this.$refs.videoRef;
      if (!video) return;
      const newTime = percent * this.duration;
      video.currentTime = newTime;
      this.currentTime = newTime;
      this.$emit('seek', newTime);
    },
    handlePrev() {
      if (this.disablePrev || !this.normalizedFeatures.prev) return;
      this.$emit('prev');
    },
    handleNext() {
      if (this.disableNext || !this.normalizedFeatures.next) return;
      this.$emit('next');
    },
    toggleFullscreen() {
      if (!this.normalizedFeatures.fullscreen) return;
      const container = this.$refs.playerContainer;
      if (!container) return;
      if (!this.isFullscreen) {
        const request =
          container.requestFullscreen ||
          container.webkitRequestFullscreen ||
          container.mozRequestFullScreen ||
          container.msRequestFullscreen;
        request && request.call(container);
      } else {
        const exit =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.mozCancelFullScreen ||
          document.msExitFullscreen;
        exit && exit.call(document);
      }
    },
    onFullscreenChange() {
      const container = this.$refs.playerContainer;
      const fullscreenEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      this.isFullscreen = fullscreenEl === container;
      this.$emit('fullscreenchange', this.isFullscreen);
    },
    handleMouseMove() {
      // 恢复鼠标显示
      this.showCursor();

      if (!this.normalizedFeatures.autoHide) {
        this.showControls = true;
        return;
      }
      this.showControls = true;
      clearTimeout(this.controlsTimer);
      this.controlsTimer = setTimeout(() => {
        this.showControls = false;
      }, 2500);

      // 设置鼠标隐藏定时器（与控件隐藏时间一致）
      this.scheduleCursorHide();
    },
    handleMouseLeave() {
      // 鼠标离开时恢复鼠标显示
      this.showCursor();

      if (!this.normalizedFeatures.autoHide) return;
      clearTimeout(this.controlsTimer);
      this.controlsTimer = setTimeout(() => {
        this.showControls = false;
      }, 800);
    },
    showCursor() {
      // 恢复鼠标显示
      if (this.originalCursor !== null) {
        document.body.style.cursor = this.originalCursor;
      } else {
        document.body.style.cursor = '';
      }
      clearTimeout(this.cursorTimer);
    },
    scheduleCursorHide() {
      // 清除之前的定时器
      clearTimeout(this.cursorTimer);

      // 保存原始鼠标样式（仅在第一次）
      if (this.originalCursor === null) {
        this.originalCursor = document.body.style.cursor || '';
      }

      // 设置延迟隐藏鼠标（与控件隐藏时间一致）
      this.cursorTimer = setTimeout(() => {
        // 只有在控件已隐藏时才隐藏鼠标
        if (!this.showControls && this.normalizedFeatures.autoHide) {
          document.body.style.cursor = 'none';
        }
      }, 2500);
    },
    handleKeydown(event) {
      if (!this.normalizedFeatures.keyboard || !this.canPlay) return;
      const tag = event.target?.tagName;
      if (tag && ['INPUT', 'TEXTAREA'].includes(tag.toUpperCase())) return;

      // 键盘操作时恢复鼠标显示
      this.showCursor();
      this.scheduleCursorHide();

      if (event.code === 'Space') {
        event.preventDefault();
        this.togglePlay();
        return;
      }
      if (event.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen();
        return;
      }
      // 方向键快进快退
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleSeekByKeyboard(event.key === 'ArrowRight' ? 1 : -1);
      }
    },
    handleSeekByKeyboard(direction) {
      const video = this.$refs.videoRef;
      if (!video || !this.duration) return;

      const seekStep = 10; // 快进/快退10秒
      const currentTime = video.currentTime || this.currentTime;
      let newTime = currentTime + (direction * seekStep);

      // 限制在有效范围内
      newTime = Math.max(0, Math.min(newTime, this.duration));

      // 更新视频时间
      video.currentTime = newTime;
      this.currentTime = newTime;

      // 触发事件
      this.$emit('seek', newTime);
    },
    applyAutoHideSetting() {
      if (!this.normalizedFeatures.autoHide) {
        this.showControls = true;
        clearTimeout(this.controlsTimer);
      }
    },
    handleResume(time) {
      const video = this.$refs.videoRef;
      if (!video) return;
      video.currentTime = time;
      this.currentTime = time;
      this.resumeTipVisible = false;
      video.play();
      this.$emit('resume', time);
    },
    handleSelectChapter(index) {
      if (index === this.currentIndex) {
        return;
      }
      this.$emit('selectChapter', index);
    },
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange);
    window.addEventListener('keydown', this.handleKeydown);
    this.applyAutoHideSetting();
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
    window.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.controlsTimer);
    clearTimeout(this.loadingTimer);
    clearTimeout(this.cursorTimer);
    // 恢复鼠标显示
    this.showCursor();
  },
};
</script>

<style scoped lang="less">
.base-video-player {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  background: #000;
  height: 100%;
  min-height: 420px;
}

.base-video-player.fullscreen {
  border-radius: 0;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.base-video-player.fullscreen .video-wrapper,
.base-video-player.fullscreen .video-element {
  min-height: 100vh;
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  z-index: 2;
}

.control-layer {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: 3;
}

.control-layer.visible {
  opacity: 1;
  pointer-events: auto;
}

.resume-tip-wrapper {
  position: absolute;
  left: 20px;
  bottom: 98px;
  z-index: 4;
}
.center-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  transition: all 0.3s ease;
  /* backdrop-filter: blur(4px); */
}

.center-play-button:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: translate(-50%, -50%) scale(1.1);
}

.center-play-button .play-icon {
  width: 40px;
  height: 40px;
  filter: brightness(0) invert(1);
}

.video-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.spinner-circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  animation: loading-bounce 1.4s ease-in-out infinite both;
}

.spinner-circle:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner-circle:nth-child(2) {
  animation-delay: -0.16s;
}

.spinner-circle:nth-child(3) {
  animation-delay: 0s;
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-text {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
</style>

